import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContactNotesTab = ({ contact, onSaveNotes }) => {
  const [notes, setNotes] = useState(contact?.notes || '');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const mockNotes = [
    {
      id: 1,
      content: `Met ${contact?.name} at the tech conference in San Francisco. Very knowledgeable about AI and machine learning. Interested in potential collaboration on upcoming projects.`,
      timestamp: new Date('2024-02-20T16:45:00'),
      type: 'meeting'
    },
    {
      id: 2,
      content: `Prefers email communication over phone calls. Usually responds within 24 hours. Best time to reach is between 9 AM - 5 PM PST.`,
      timestamp: new Date('2024-02-15T10:30:00'),
      type: 'preference'
    },
    {
      id: 3,
      content: `Birthday reminder: Loves chocolate cake and outdoor activities. Consider sending a hiking-themed birthday card next year.`,
      timestamp: new Date('2024-01-10T14:20:00'),
      type: 'personal'
    }
  ];

  const [notesList, setNotesList] = useState(mockNotes);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('general');

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSaveNotes(notes);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving notes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setNotes(contact?.notes || '');
    setIsEditing(false);
  };

  const handleAddNote = () => {
    if (newNote?.trim()) {
      const note = {
        id: Date.now(),
        content: newNote?.trim(),
        timestamp: new Date(),
        type: noteType
      };
      
      setNotesList(prev => [note, ...prev]);
      setNewNote('');
      setNoteType('general');
    }
  };

  const handleDeleteNote = (noteId) => {
    setNotesList(prev => prev?.filter(note => note?.id !== noteId));
  };

  const getNoteTypeIcon = (type) => {
    switch (type) {
      case 'meeting': return 'Users';
      case 'preference': return 'Settings';
      case 'personal': return 'Heart';
      case 'business': return 'Briefcase';
      case 'reminder': return 'Bell';
      default: return 'FileText';
    }
  };

  const getNoteTypeColor = (type) => {
    switch (type) {
      case 'meeting': return 'bg-primary/10 text-primary border-primary/20';
      case 'preference': return 'bg-secondary/10 text-secondary border-secondary/20';
      case 'personal': return 'bg-accent/10 text-accent border-accent/20';
      case 'business': return 'bg-success/10 text-success border-success/20';
      case 'reminder': return 'bg-warning/10 text-warning border-warning/20';
      default: return 'bg-muted text-text-secondary border-border';
    }
  };

  return (
    <div className="p-6">
      {/* Add New Note Section */}
      <div className="mb-8 p-4 bg-muted/30 rounded-lg border border-border">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Add New Note</h3>
        
        <div className="space-y-4">
          {/* Note Type Selector */}
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'general', label: 'General', icon: 'FileText' },
              { id: 'meeting', label: 'Meeting', icon: 'Users' },
              { id: 'preference', label: 'Preference', icon: 'Settings' },
              { id: 'personal', label: 'Personal', icon: 'Heart' },
              { id: 'business', label: 'Business', icon: 'Briefcase' },
              { id: 'reminder', label: 'Reminder', icon: 'Bell' }
            ]?.map((type) => (
              <button
                key={type?.id}
                onClick={() => setNoteType(type?.id)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-micro ${
                  noteType === type?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-surface text-text-secondary hover:text-text-primary hover:bg-muted'
                }`}
              >
                <Icon name={type?.icon} size={16} />
                <span>{type?.label}</span>
              </button>
            ))}
          </div>

          {/* Note Input */}
          <div className="relative">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e?.target?.value)}
              placeholder={`Add a ${noteType} note about ${contact?.name}...`}
              className="w-full h-24 px-4 py-3 border border-border rounded-lg bg-surface text-text-primary placeholder-text-secondary resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <div className="absolute bottom-3 right-3 text-xs text-text-secondary">
              {newNote?.length}/500
            </div>
          </div>

          {/* Add Button */}
          <div className="flex justify-end">
            <Button
              variant="default"
              size="sm"
              onClick={handleAddNote}
              disabled={!newNote?.trim()}
              iconName="Plus"
              iconPosition="left"
            >
              Add Note
            </Button>
          </div>
        </div>
      </div>
      {/* Notes List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text-primary">
            Notes ({notesList?.length})
          </h3>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              iconName="Search"
              iconPosition="left"
            >
              Search Notes
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              iconName="Filter"
              iconPosition="left"
            >
              Filter
            </Button>
          </div>
        </div>

        {notesList?.length > 0 ? (
          <div className="space-y-4">
            {notesList?.map((note) => (
              <div key={note?.id} className="bg-surface border border-border rounded-lg p-4 hover:shadow-card transition-micro">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getNoteTypeColor(note?.type)}`}>
                      <Icon name={getNoteTypeIcon(note?.type)} size={12} className="mr-1" />
                      {note?.type?.charAt(0)?.toUpperCase() + note?.type?.slice(1)}
                    </span>
                    
                    <span className="text-sm text-text-secondary">
                      {note?.timestamp?.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>

                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {}}
                      className="h-8 w-8"
                    >
                      <Icon name="Edit" size={14} />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteNote(note?.id)}
                      className="h-8 w-8 text-error hover:text-error"
                    >
                      <Icon name="Trash2" size={14} />
                    </Button>
                  </div>
                </div>

                <p className="text-text-primary leading-relaxed">
                  {note?.content}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="FileText" size={24} className="text-text-secondary" />
            </div>
            <h4 className="text-lg font-medium text-text-primary mb-2">No Notes Yet</h4>
            <p className="text-text-secondary mb-4">
              Add your first note about {contact?.name} to keep track of important information.
            </p>
          </div>
        )}
      </div>
      {/* Legacy Notes Section (if exists) */}
      {contact?.notes && (
        <div className="mt-8 pt-6 border-t border-border">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-md font-semibold text-text-primary">Legacy Notes</h4>
            
            {!isEditing ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(true)}
                iconName="Edit"
                iconPosition="left"
              >
                Edit
              </Button>
            ) : (
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSave}
                  loading={isSaving}
                  iconName="Save"
                  iconPosition="left"
                >
                  Save
                </Button>
              </div>
            )}
          </div>

          {isEditing ? (
            <textarea
              value={notes}
              onChange={(e) => setNotes(e?.target?.value)}
              placeholder={`Add notes about ${contact?.name}...`}
              className="w-full h-32 px-4 py-3 border border-border rounded-lg bg-surface text-text-primary placeholder-text-secondary resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          ) : (
            <div className="bg-muted/50 rounded-lg p-4">
              {contact?.notes ? (
                <p className="text-text-primary leading-relaxed whitespace-pre-wrap">
                  {contact?.notes}
                </p>
              ) : (
                <p className="text-text-secondary italic">
                  No legacy notes available.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactNotesTab;